<template>
  <div v-if="!isStockOrder" data-qa="company-details">
    <div v-if="hasCompany">
      <horizontal-line />
      <h4>{{ i18n.t('/Company/BlockTitle') }}</h4>
      <div>
        <company-details :company="company" />

        <div class="company-actions-row">
          <app-button
            :disabled="!isEditable"
            data-qa="edit-company-button"
            button-class="action-primary"
            @click="toggleEditModal(true)"
          >
            <svg
              width="15"
              height="15"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="2.2"
              aria-hidden="true"
            >
              <path d="M12 20h9"></path>
              <path d="M16.5 3.5a2.121 2.121 0 0 1 3 3L7 19l-4 1 1-4Z"></path>
            </svg>
            <span>{{ i18n.t('/Company/EditCompany') }}</span>
          </app-button>
          <app-button
            :disabled="!isEditable"
            data-qa="deselect-company-button"
            button-class="action-quiet"
            @click="deselectCompany()"
          >
            <svg
              width="15"
              height="15"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="2.2"
              aria-hidden="true"
            >
              <line x1="18" y1="6" x2="6" y2="18"></line>
              <line x1="6" y1="6" x2="18" y2="18"></line>
            </svg>
            <span>{{ i18n.t('/Company/DeselectCompany') }}</span>
          </app-button>
        </div>
      </div>
    </div>
    <div v-else>
      <app-button
        :disabled="!isEditable"
        data-qa="add-company-button"
        button-class="search-action"
        @click="addCompanyClick()"
      >
        <svg
          width="16"
          height="16"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          stroke-width="2.2"
          aria-hidden="true"
        >
          <circle cx="11" cy="11" r="7"></circle>
          <line x1="21" y1="21" x2="16.65" y2="16.65"></line>
        </svg>
        <span>{{ i18n.t('/Company/AddCompany') }}</span>
      </app-button>
    </div>
    <add-company-extra-details />
    <horizontal-line v-if="hasCompany && !hasLeaseCompany" />

    <add-lease-company :is-b2b="false" />

    <search-company-modal
      :show="showModal"
      :page-type="pageTypes.QUOTE"
      @close="toggleModal(false)"
    />

    <modal
      v-if="showEditModal"
      :extra-wide="true"
      :title="i18n.t('/Company/EditCompanyModalTitle')"
      @close="toggleEditModal(false)"
    >
      <company-form :company-to-edit="company" @on-submit="toggleEditModal(false)" />
    </modal>
  </div>
</template>

<script>
import Modal from '@/components/generic/presentational/molecules/modal/Modal.vue'
import AppButton from '@/components/generic/presentational/atoms/appButton/AppButton.vue'
import HorizontalLine from '@/components/generic/presentational/atoms/horizontalLine/HorizontalLine.vue'
import CompanyDetails from '@/components/generic/presentational/molecules/companyDetails/CompanyDetails.vue'
import SearchCompanyModal from '@/components/generic/presentational/organisms/searchCompanyModal/SearchCompanyModal.vue'
import CompanyForm from '@/components/generic/presentational/organisms/companyForm/CompanyForm.vue'
import FleetSalesContracts from '@/api/server/FleetSalesContracts'
import StoreConnector from '@/helpers/StoreConnector'
import CompaniesApi from '@/api/server/Companies'
import { mapState } from 'vuex'
import { EventBus } from '@/plugins/event-bus'
import { pageTypes } from '@/helpers/pageTypeConstants'
import AddLeaseCompany from '@/components/generic/presentational/organisms/addLeaseCompany/AddLeaseCompany.vue'
import AddCompanyExtraDetails from '@/components/generic/presentational/organisms/addCompany/AddCompanyExtraDetails.vue'

export default {
  components: {
    AddCompanyExtraDetails,
    AddLeaseCompany,
    AppButton,
    Modal,
    SearchCompanyModal,
    CompanyDetails,
    CompanyForm,
    HorizontalLine,
  },
  data() {
    return {
      i18n: window.VUE_CLI_EXPORTS.plugins.i18n,
      showModal: false,
      showEditModal: false,
      hasCompany: false,
      hasLeaseCompany: false,
      isEditable: true,
      company: null,
      isStockOrder: false,
    }
  },
  computed: {
    pageTypes() {
      return pageTypes
    },
    ...mapState('quote', ['quote']),
    quoteId() {
      return this.quote?.QuoteId
    },
  },
  created() {
    StoreConnector.get('quote/customerCompany', (data) => {
      this.hasCompany = data?.CustomerCompanyName != null
      this.company = data
    })
    StoreConnector.get('quote/quoteStatusIsSalesOrder', (data) => {
      this.isEditable = !data
    })
    StoreConnector.get('quote/leaseCompany', (data) => {
      this.hasLeaseCompany = data?.Name != null
    })
    StoreConnector.get('quote/isStockOrder', (data) => {
      this.isStockOrder = data
    })
  },
  methods: {
    toggleModal(show) {
      this.showModal = show

      if (!show) {
        StoreConnector.dispatch('companies/resetRecommendedCompany')
      }
    },
    toggleEditModal(show) {
      this.showEditModal = show
    },
    deselectCompany() {
      StoreConnector.dispatch('quote/resetCustomerCompany')
      FleetSalesContracts.removeContract(this.quoteId)
      StoreConnector.dispatch('quote/resetFleetContract')
      EventBus.$emit('quote/companyRemoved')
    },
    async addCompanyClick() {
      this.toggleModal(true)
      await this.searchRecommendedCompany()
    },
    async searchRecommendedCompany() {
      const companyNumber = StoreConnector.getOnce('quote/customer')?.CustomerCompanyMagicNumber
      if (companyNumber == null) return
      const results = await CompaniesApi.searchCompany(companyNumber)
      if (results.length > 0) StoreConnector.dispatch('companies/setRecommendedCompany', results[0])
    },
  },
}
</script>

<style lang="scss" scoped>
.company-actions-row {
  display: flex;
  gap: 8px;
}
</style>
